<template>
  <AppShell title="Lånelöfte">
    <template #actions>
      <AppRouterLink to="/ansokningar" variant="button" class="back-link"> Tillbaka till ansökningar </AppRouterLink>
    </template>

    <div ref="stickyHeaderEl" class="sticky-header">
      <ApplicationHeader :application-id="applicationId" />

      <AppTabs :is-loading="isLoading" class="app-tabs-override">
        <template #left>
          <AppRouterLink
            :to="basePath"
            variant="nav"
            class="nav-link"
            exact-active-class="nav-link-active font-semibold"
          >
            Ansökan
          </AppRouterLink>
        </template>
        <template #right>
          <AppRouterLink
            :to="`${basePath}/dokument`"
            variant="nav"
            class="nav-link"
            active-class="nav-link-active font-semibold"
          >
            Dokument
          </AppRouterLink>
        </template>
      </AppTabs>
    </div>

    <router-view />
  </AppShell>
</template>

<script setup lang="ts">
import { computed, onMounted, provide, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { AppTabs } from '@staff/ui'
import AppShell from '@/components/AppShell.vue'
import AppRouterLink from '@/components/AppRouterLink.vue'
import ApplicationHeader from '@/views/application/ApplicationHeader.vue'
import { provideApplicationContext, provideApplicationKalpContext } from '@/views/application/application.context'
import { useApplicationKalp } from '@/composables/applicationKalp'
import { provideApplicationDocumentsContext } from '@/views/application/applicationDocuments.context'
import { useStickyHeader } from '@/composables/useStickyHeader'

import { fetchCommitmentApplicationById, type DistributorCommitmentDetails } from '@/services/commitment'

const route = useRoute()

const isLoading = ref(true)
const error = ref('')
const application = ref<DistributorCommitmentDetails | null>(null)

const applicationId = computed(() => {
  const id = route.params.id
  return Array.isArray(id) ? (id[0] ?? '') : String(id ?? '')
})
const basePath = computed(() => (applicationId.value ? `/lofte/${applicationId.value}` : '/lofte'))
const { kalp, isKalpLoading, kalpError, reloadKalp } = useApplicationKalp(applicationId)
provideApplicationKalpContext({ kalp, isKalpLoading, kalpError })
provideApplicationContext({
  application,
  isLoading,
  error,
})
provideApplicationDocumentsContext(applicationId)

const { stickyHeaderEl, observeHeader } = useStickyHeader(isLoading)

const loadApplication = async () => {
  if (!applicationId.value) {
    error.value = 'Ansökan saknar ID i URL.'
    isLoading.value = false
    return
  }

  application.value = null
  isLoading.value = true
  error.value = ''

  try {
    application.value = await fetchCommitmentApplicationById(applicationId.value)
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Ett fel uppstod när ansökan hämtades.'
  } finally {
    isLoading.value = false
  }
}

provide('refreshApplication', () => Promise.all([loadApplication(), reloadKalp()]))

watch(applicationId, loadApplication)

onMounted(async () => {
  await loadApplication()
  observeHeader()
})
</script>

<style scoped lang="scss">
.sticky-header {
  position: sticky;
  /* Vertical offsets must match .app-shell-content's padding-top (12px, 8px on
     small screens), horizontal must match .app-shell-main's padding (24px/16px). */
  top: -12px;
  z-index: 10;
  background-color: var(--color-canvas-inset); /* matches AppShell main */
  margin: -12px -24px 0;
  padding: 12px 24px 0;
}

.app-tabs-override :deep(.app-tabs-wrapper) {
  position: static;
}

.back-link:hover,
.nav-link:hover {
  background-color: var(--color-button-default-hover-bg);
}

.nav-link-active {
  background-color: var(--color-brand-subtle);
  color: var(--color-brand-default);
}

@media (max-width: 767px) {
  .sticky-header {
    top: -8px;
    margin: -8px -16px 0;
    padding: 8px 16px 0;
  }
}
</style>
